import React, { useEffect, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { fetchStaffTeam, inviteStaffMember, type StaffMember } from '../../utils/staffTeam';
import type { StaffRole } from '../../utils/staffAuth';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from '../../components/portal/adminStyles';

/**
 * Portal Productions staff accounts. Owners only — the list and the invitation
 * both come from the staff-team Edge Function, which re-checks the role
 * server-side, so hiding the page is presentation rather than protection.
 */
export function Team() {
  const {
    staffUser,
    notify,
    goPortal
  } = usePortal();
  const [members, setMembers] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<StaffRole>('staff');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  async function load() {
    setLoading(true);
    const result = await fetchStaffTeam();
    if (!result.ok) setLoadError(result.error ?? 'The team could not be loaded.');else {
      setLoadError('');
      setMembers(result.members ?? []);
    }
    setLoading(false);
  }
  useEffect(() => {
    void load();
  }, []);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    const address = email.trim().toLowerCase();
    const result = await inviteStaffMember(address, role);
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'Invitation could not be sent.');
      return;
    }
    notify(`Invitation sent to ${address}`);
    setEmail('');
    setRole('staff');
    setOpen(false);
    void load();
  }
  if (staffUser?.role !== 'owner') {
    return <section aria-labelledby="team" data-id="element-797" data-mp="3q2cip5v2misf">
        <h1 id="team" className="font-display text-4xl leading-none tracking-tight sm:text-5xl" data-id="element-798" data-mp="36y92zv4ig3xo">
          Team
        </h1>
        <p className="mt-8 font-display text-xl text-paper/40" data-id="element-799" data-mp="326rck1045lqy">
          Only Portal owners can manage staff accounts.
        </p>
      </section>;
  }
  return <section aria-labelledby="team" data-id="element-800" data-mp="2s9mkjkjl6w28">
      <button type="button" onClick={() => goPortal('projects')} className="label text-paper/35 transition-colors duration-150 ease-out hover:text-paper" data-id="element-801" data-mp="2sanz3yzta0op">
        ← Client Projects
      </button>

      <h1 id="team" className="mt-10 font-display text-4xl leading-none tracking-tight sm:text-5xl" data-id="element-802" data-mp="3kz1wccj2ssz7">
        Team
      </h1>
      <p className="label-sm mt-6 text-paper/40" data-id="element-803" data-mp="3b0m8cifchztx">Portal Productions staff accounts.</p>

      <ul className="m-0 mt-14 list-none p-0" data-id="element-804" data-mp="24jsdjjly81pw">
        {loading && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-805" data-mp="24hxpvxjalvi4">
            Loading team…
          </li>}

        {!loading && loadError && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-806" data-mp="f4hkf50hgort">
            {loadError}
          </li>}

        {!loading && !loadError && members.map(member => <li key={member.userId} className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-3 border-b border-ruleDark py-7" data-id="element-807" data-mp="oz9jkd9prjop">
              <div className="min-w-0" data-id="element-808" data-mp="ysyrtdlmgc5o">
                <p className="m-0 font-display text-2xl leading-none" data-id="element-809" data-mp="12ry4xlhibf87">{member.name}</p>
                <p className="label-sm mt-3 text-paper/30" data-id="element-810" data-mp="17rty0poo1m8z">{member.email}</p>
              </div>
              <div className="flex items-baseline gap-8" data-id="element-811" data-mp="1cqnaces7v77k">
                {member.pending && <span className="label-sm text-paper/30" data-id="element-812" data-mp="1cokn22ig7s6j">Invited</span>}
                <span className="label text-paper/50" data-id="element-813" data-mp="39k8m66hszmxu">{member.role}</span>
              </div>
            </li>)}
      </ul>

      {open ? <form onSubmit={submit} className="mt-12 max-w-xl" data-id="element-814" data-mp="18trhc8s8o6sn">
          <p className="label m-0 text-paper/40" data-id="element-815" data-mp="18rwrqh60vqi8">Invite Team Member</p>

          <label className="mt-9 block" data-id="element-816" data-mp="13tlj29rgrm1l">
            <span className="label block text-paper/40" data-id="element-817" data-mp="13wr6zlocwhvq">Email</span>
            <input type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="e.g. name@portalproductions.me" autoFocus className={FIELD} data-id="element-818" data-mp="1ht4rl3o6szef" />
          </label>

          <label className="mt-9 block" data-id="element-819" data-mp="l586pmlpvhxe">
            <span className="label block text-paper/40" data-id="element-820" data-mp="l22i08h02i27">Role</span>
            <select value={role} onChange={event => setRole(event.target.value as StaffRole)} className={SELECT} data-id="element-821" data-mp="ywdbf33xz0el">
              <option value="staff" className={OPTION} data-id="element-822" data-mp="6er3bxpiuhxv">
                Staff
              </option>
              <option value="owner" className={OPTION} data-id="element-823" data-mp="2e766u4y7ebq">
                Owner
              </option>
            </select>
          </label>

          {error && <p role="alert" className="label-sm mt-8 text-paper" data-id="element-824" data-mp="3oda9xyljh411">
              {error}
            </p>}

          <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-825" data-mp="3egys6t36jlz7">
            <button type="submit" disabled={busy} className={BUTTON_SOLID} data-id="element-826" data-mp="3eis00bpj4c8s">
              Send Invitation
            </button>
            <button type="button" onClick={() => setOpen(false)} className={BUTTON_QUIET} data-id="element-827" data-mp="b08io48e2qsr">
              Cancel
            </button>
          </div>
        </form> : <button type="button" onClick={() => setOpen(true)} className={`${BUTTON_GHOST} mt-12 inline-flex items-center gap-3`} data-id="element-828" data-mp="fx8qajkw01lz">
          <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-829" data-mp="5zub0nap9iv9" />
          Invite Team Member
        </button>}
    </section>;
}